import * as React from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Formik } from "formik";
import { AppConfiguration, PublicRoutes, QueryParamKeys, QueryParamValues, StatusCode } from "src/common";
import { Message } from "src/common/app/message-item.class";
import { MessageHandlerDisplayType } from "src/common/constants/constants";
import { DeveloperErrorMessages, DeveloperMessageAnchors, DeveloperSuccessMessages, DeveloperWarningMessages, useCreateAccount } from "src/common/developers";
import { uiActions } from "src/store";
import * as yup from "yup";

function MultipleUserFormComponent() {
  const default_domain = "testing_@developer.com".replace(/@/, self.crypto.randomUUID().slice(0, 4) + "@");
  const default_display_name = "Testing User";
  const default_user_number = 1;
  const dispatch = useDispatch();
  const { createUser, success, setSuccess } = useCreateAccount();
  const schema = yup.object().shape({
    domain: yup.string().required("User ID domain is required"),
    display_name: yup.string().required("Display name is required"),
    user_number: yup
      .number()
      .min(1, 'Invalid number. Please pick a number between 1 and 30')
      .max(30, 'Invalid number. Please pick a number between 1 and 30')
      .required('Invalid number. Please pick a number between 1 and 30'),
  });

  function handleError(error: any, user_number: number, user_id: string) {
    switch (error.status) {
      case StatusCode.FORBIDDEN_DOMAIN:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_forbidden, MessageHandlerDisplayType.toastr)));
        break;
      case StatusCode.NO_EMAIL_SIGNATURE_CODE:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_API_error, MessageHandlerDisplayType.toastr)));
        break;
      case StatusCode.CONFLICT_CODE:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_user_already_exists_multiple.replace(DeveloperMessageAnchors.user_id, user_id || "").replace(DeveloperMessageAnchors.user_count, user_number?.toString() || ""), MessageHandlerDisplayType.toastr)));
        break;
      default:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_creating_multiple_users.replace(DeveloperMessageAnchors.user_count, user_number?.toString() || ""), MessageHandlerDisplayType.toastr)));
    }
  }

  async function handleMultipleSubmit(user_number: number, display_name: string, domain: string) {
    const is_web = AppConfiguration.buildForWeb();
    if (is_web) {
      dispatch(uiActions.handleRequestErrors(new Message(DeveloperWarningMessages.warning_wrong_port, MessageHandlerDisplayType.toastr)));
      return;
    }
    let success = true;
    let created_count = 0;
    for (let i = 1; i <= user_number; i++) {
      const user_id = domain.replace(/@/, "+" + i.toString() + "@");
      const display_name_with_index = `${display_name} ${i}`;
      try {
        await createUser(user_id, display_name_with_index);
        created_count++;
      } catch (error: any) {
        handleError(error, created_count, user_id);
        success = false;
        break;
      }
    }
    if (success) {
      setSuccess(true);
      dispatch(uiActions.handleSetMessage(new Message(DeveloperSuccessMessages.success_creating_multiple_users.replace(DeveloperMessageAnchors.user_count, created_count.toString()))));
    }
  }

  return (
    <Formik
      validationSchema={schema}
      initialValues={{ user_number: default_user_number, display_name: default_display_name, domain: default_domain }}
      onSubmit={async (values, { setSubmitting }) => {
        const user_number = values.user_number;
        const display_name = values.display_name;
        const domain = values.domain;
        await handleMultipleSubmit(user_number, display_name, domain);
        setSubmitting(false);
      }}
    >
      {({ handleSubmit, handleChange, handleBlur, values, errors, touched, isSubmitting }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="domain">User ID Prefix</Form.Label>
            <Form.Control
              id="domain"
              name="domain"
              type="text"
              placeholder={default_domain}
              value={values.domain}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!errors.domain && touched.domain}
            />
            <Form.Control.Feedback type="invalid">
              {errors.domain}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="display_name">Display Name Prefix</Form.Label>
            <Form.Control
              id="display_name"
              name="display_name"
              type="text"
              placeholder={default_display_name}
              value={values.display_name}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!errors.display_name && touched.display_name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.display_name}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="user_number">Number of Users</Form.Label>
            <Form.Control
              id="user_number"
              name="user_number"
              type="number"
              min={1}
              max={30}
              placeholder={default_user_number.toString()}
              value={values.user_number}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!errors.user_number && touched.user_number}
            />
            <Form.Control.Feedback type="invalid">
              {errors.user_number}
            </Form.Control.Feedback>
          </Form.Group>
          <Alert variant="info" className="mb-3">
            User IDs are created with a +1, +2, +3, etc. suffix to avoid conflicts. <br />
          </Alert>
          <Button type="submit" className="me-2" disabled={isSubmitting}>Create Users</Button>
          {success && <Button variant="link" href={`/${PublicRoutes.choose_account_route}?${QueryParamKeys.PAGE_ACTION_QUERY_KEY}=${QueryParamValues.switch}`}>Choose Account</Button>}
        </Form>
      )}
    </Formik>
  );
}

export default React.memo(MultipleUserFormComponent); 
